import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AUDIT_LIST_TEST_IDS } from '@amzn/gravion-ui-contracts';
import { Col } from '@amzn/stencil-react-components/layout';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';
import { Text } from '@amzn/stencil-react-components/text';

import { AuditQueryKeys, useSearchAuditsQuery } from '../../features/audit/api';
import { useAuditListFilters } from '../../features/audit/hooks/useAuditListFilters';
import { AuditListFilterBar } from '../../features/audit/components/AuditListFilterBar';
import { CreateAuditSheet } from '../../features/audit/components/CreateAuditSheet';
import { AuditCard } from '../../features/audit/components/AuditCard';
import { CardSkeleton } from '../../shared/ui/CardSkeleton';
import { RefreshButton } from '../../shared/ui/RefreshButton';
import { useInfiniteScrollTrigger } from '../../shared/ui/useInfiniteScrollTrigger';
import { trackInteraction, INTERACTION_TYPES } from '../../shared/observability';
import { AUDIT_PAGES, AUDIT_ACTIONS } from '../../features/audit/tracking';
import { useCurrentUser, usePermissions } from '../../shared/auth';
import { useTemplatesQuery } from '../../shared/data/templates/useTemplatesQuery';
import { useDivisionsQuery } from '../../shared/data/divisions/useDivisionsQuery';
import { useMessages } from '../../shared/i18n/useMessages';
import { PreferredLocationBanner } from '../../shared/ui/PreferredLocationBanner';

const LIST_BUNDLE = 'features.audit.translations.auditList';
type ListKey = 'emptyStateMessage' | 'loadError' | 'refreshAriaLabel';

const SKELETON_COUNT = 5;

/**
 * Mobile audit list page.
 *
 * Features:
 * - Keyword / assignee search with mode toggle (via AuditListFilterBar)
 * - FilterBar (status, audit type, location, template, division) with flyout
 * - Infinite scroll via cursor-based pagination
 * - Skeleton loading on initial fetch and next-page fetch
 * - RUM interaction tracking on search, filter changes, and card taps
 */
export function AuditsPage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useMessages<ListKey>(LIST_BUNDLE);
  const { businessUnit, programId } = useCurrentUser();
  const { audit: auditPermissions } = usePermissions();
  const { filters, setFilter, applyFilters, setKeyword, commandInput, locationFilter } =
    useAuditListFilters();
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);

  const { data: templatesData } = useTemplatesQuery({
    businessUnitId: businessUnit ?? '',
  });
  const { data: divisionsData } = useDivisionsQuery({
    businessUnitId: businessUnit ?? '',
    programId: programId ?? '',
  });

  const templateOptions = (templatesData?.auditTemplates ?? []).map((tmpl) => ({
    label: tmpl.templateName,
    value: tmpl.id,
  }));
  const divisionOptions = (divisionsData?.divisions ?? []).map((d) => ({
    label: d.divisionName ?? '',
    value: d.divisionName ?? '',
  }));

  const {
    data,
    isLoading,
    isPending,
    isError,
    error,
    isFetching,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useSearchAuditsQuery(commandInput, {
    // Gate until user context resolves — createInfiniteQuery has no
    // built-in filter gating, so the caller owns the enabled flag.
    enabled: !!commandInput.businessUnitId,
  });

  const sentinelRef = useInfiniteScrollTrigger({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage: () => void fetchNextPage(),
  });

  useEffect(() => {
    trackInteraction({ type: INTERACTION_TYPES.pageViewed, page: AUDIT_PAGES.list });
  }, []);

  // Manual refresh — invalidates the whole `audits` collection so ALL
  // paginated pages are refetched from scratch. The spinning state is
  // derived from react-query's `isFetching`, minus the two fetches it
  // doesn't represent: initial load (rendered as skeletons) and
  // next-page loads (rendered as sentinel spinner). RefreshButton
  // disables while `isRefreshing`, so a rapid double-tap can't queue
  // a redundant invalidation on top of the one already in flight
  // (async-interleaving guideline). Mirrors the TasksPage TL-06 impl.
  const queryClient = useQueryClient();
  const isRefreshing = isFetching && !isLoading && !isFetchingNextPage;

  const handleRefresh = useCallback((): void => {
    trackInteraction({
      type: INTERACTION_TYPES.actionTriggered,
      page: AUDIT_PAGES.list,
      action: AUDIT_ACTIONS.refreshList,
    });
    void queryClient.invalidateQueries({ queryKey: [AuditQueryKeys.lists] });
  }, [queryClient]);

  const handleCardTap = useCallback(
    (auditId: string) => {
      trackInteraction({
        type: INTERACTION_TYPES.cardTapped,
        page: AUDIT_PAGES.list,
        entityId: auditId,
        entityType: 'audit',
      });
      void navigate(`/audits/details?auditId=${auditId}`);
    },
    [navigate],
  );

  const handleOpenCreateSheet = useCallback(() => {
    trackInteraction({
      type: INTERACTION_TYPES.actionTriggered,
      page: AUDIT_PAGES.list,
      action: 'open-create-audit-sheet',
    });
    setIsCreateSheetOpen(true);
  }, []);

  const handleCloseCreateSheet = useCallback(() => {
    setIsCreateSheetOpen(false);
  }, []);

  const audits = data?.pages.flatMap((page) => page.audits ?? []) ?? [];

  return (
    <Col
      dataTestId={AUDIT_LIST_TEST_IDS.root}
      gridGap="dimensions.spacing.300"
      width="100%"
      minWidth={0}
      padding={{
        top: 'dimensions.spacing.300',
        left: 'dimensions.spacing.200',
        right: 'dimensions.spacing.200',
      }}
    >
      <PreferredLocationBanner />

      <AuditListFilterBar
        filters={filters}
        locationFilter={locationFilter}
        templateOptions={templateOptions}
        divisionOptions={divisionOptions}
        showOverflowMenu={auditPermissions.canCreateAnyAudit}
        onCreateAudit={handleOpenCreateSheet}
        rightSlot={
          <RefreshButton
            dataTestId={AUDIT_LIST_TEST_IDS.refreshControl}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            ariaLabel={t('refreshAriaLabel')}
          />
        }
        setFilter={setFilter}
        applyFilters={applyFilters}
        setKeyword={setKeyword}
      />

      {isError && (
        <MessageBanner
          dataTestId={AUDIT_LIST_TEST_IDS.errorBanner}
          type={MessageBannerType.Error}
          isDismissible={false}
        >
          {error?.message ?? t('loadError')}
        </MessageBanner>
      )}

      {isLoading && (
        <Col gridGap="dimensions.spacing.300">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            // eslint-disable-next-line @eslint-react/no-array-index-key -- static skeleton placeholders never reorder
            <CardSkeleton key={i} dataTestId={AUDIT_LIST_TEST_IDS.initialSkeleton(i)} />
          ))}
        </Col>
      )}

      {!isLoading && audits.length > 0 && (
        <Col gridGap="dimensions.spacing.300">
          {audits.map((audit) => (
            <AuditCard key={audit.auditId} audit={audit} onTap={handleCardTap} />
          ))}
          <div ref={sentinelRef} />
          {isFetchingNextPage && <CardSkeleton />}
        </Col>
      )}

      {/* isPending guards the disabled-query case (no businessUnit yet) —
          without it the empty state would show before the query ever runs */}
      {!isLoading && !isPending && !isError && audits.length === 0 && (
        <Col
          dataTestId={AUDIT_LIST_TEST_IDS.emptyState}
          padding="dimensions.spacing.500"
          alignItems="center"
        >
          <Text color="color.text.secondary">{t('emptyStateMessage')}</Text>
        </Col>
      )}

      <CreateAuditSheet isOpen={isCreateSheetOpen} onClose={handleCloseCreateSheet} />
    </Col>
  );
}
